import {useCallback, useEffect, useState} from 'react';
import {DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors} from '@dnd-kit/core';
import {
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    rectSortingStrategy,
    horizontalListSortingStrategy,
    arrayMove,
} from '@dnd-kit/sortable';
import {CSS} from '@dnd-kit/utilities';
import {apiFetch, isAuthError} from '../api';
import {useToast} from './Toast';
import Spinner from './Spinner';
import TemplateCard from './TemplateCard';
import NewTemplateModal from './NewTemplateModal';
import NewChecklistModal from './NewChecklistModal';
import {GridIcon, ColumnsIcon} from './icons';

function SortableTemplateCard({template, ...props}) {
    const {attributes, listeners, setNodeRef, transform, transition, isDragging} = useSortable({id: template.id});
    const {role, 'aria-disabled': _ariaDisabled, ...safeAttrs} = attributes;
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: isDragging ? 10 : undefined,
        opacity: isDragging ? 0.5 : 1,
    };
    return (
        <div ref={setNodeRef} style={style} className="sortable-template-wrapper" {...safeAttrs}>
            <TemplateCard data={template} isDragEnabled dragListeners={listeners} {...props} />
        </div>
    );
}

export default function TemplateContainer({groups, onSpawn}) {
    const showToast = useToast();
    const onError = useCallback(
        err => {
            if (!isAuthError(err)) showToast(err.message);
        },
        [showToast],
    );
    const [loading, setLoading] = useState(true);
    const [templates, setTemplates] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [viewMode, setViewMode] = useState(() => localStorage.getItem('templateViewMode') || 'grid');
    const [spawnTemplateId, setSpawnTemplateId] = useState(null);

    useEffect(() => {
        const controller = new AbortController();
        apiFetch('/checklist-templates', {signal: controller.signal})
            .then(res => res.json())
            .then(data => {
                setTemplates(data);
                setLoading(false);
            })
            .catch(err => {
                if (err.name !== 'AbortError') {
                    onError(err);
                    setLoading(false);
                }
            });
        return () => controller.abort();
    }, [onError]);

    const handleCreate = useCallback(
        async (name, description) => {
            try {
                const res = await apiFetch('/checklist-templates', {
                    method: 'POST',
                    headers: {'Content-Type': 'application/json'},
                    body: JSON.stringify({name, description}),
                });
                const created = await res.json();
                setTemplates(prev => [...prev, created]);
                setShowModal(false);
            } catch (err) {
                onError(err);
            }
        },
        [onError],
    );

    const handleUpdateTemplate = useCallback(
        async (id, name, description) => {
            try {
                const res = await apiFetch(`/checklist-templates/${id}`, {
                    method: 'PUT',
                    headers: {'Content-Type': 'application/json'},
                    body: JSON.stringify({name, description}),
                });
                const updated = await res.json();
                setTemplates(prev =>
                    prev.map(t => (t.id === id ? {...t, name: updated.name, description: updated.description} : t)),
                );
            } catch (err) {
                onError(err);
            }
        },
        [onError],
    );

    const handleDelete = useCallback(
        async id => {
            try {
                await apiFetch(`/checklist-templates/${id}`, {method: 'DELETE'});
                setTemplates(prev => prev.filter(t => t.id !== id));
            } catch (err) {
                onError(err);
            }
        },
        [onError],
    );

    const handleUse = useCallback(id => {
        setSpawnTemplateId(id);
    }, []);

    const handleSpawnConfirm = useCallback(
        async (title, _templateId, groupId) => {
            setSpawnTemplateId(null);
            try {
                const res = await apiFetch(`/checklist-templates/${_templateId}/spawn`, {
                    method: 'POST',
                    headers: {'Content-Type': 'application/json'},
                    body: JSON.stringify({name: title, groupId}),
                });
                const checklist = await res.json();
                if (onSpawn) onSpawn(checklist);
            } catch (err) {
                onError(err);
            }
        },
        [onSpawn, onError],
    );

    const handleExpand = useCallback(
        async id => {
            try {
                const res = await apiFetch(`/checklist-templates/${id}`);
                return await res.json();
            } catch (err) {
                onError(err);
            }
        },
        [onError],
    );

    const handleAddItem = useCallback(
        async (templateId, text) => {
            try {
                const res = await apiFetch(`/checklist-templates/${templateId}/items`, {
                    method: 'POST',
                    headers: {'Content-Type': 'application/json'},
                    body: JSON.stringify({text}),
                });
                return await res.json();
            } catch (err) {
                onError(err);
            }
        },
        [onError],
    );

    const handleUpdateItem = useCallback(
        async (templateId, itemId, text) => {
            try {
                const res = await apiFetch(`/checklist-templates/${templateId}/items/${itemId}`, {
                    method: 'PUT',
                    headers: {'Content-Type': 'application/json'},
                    body: JSON.stringify({text}),
                });
                return await res.json();
            } catch (err) {
                onError(err);
            }
        },
        [onError],
    );

    const handleReorderItems = useCallback(
        (templateId, movedItemId, afterId) => {
            apiFetch(`/checklist-templates/${templateId}/items/${movedItemId}`, {
                method: 'PUT',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({after: afterId}),
            }).catch(err => onError(err));
        },
        [onError],
    );

    const handleDeleteItem = useCallback(
        async (templateId, itemId) => {
            try {
                await apiFetch(`/checklist-templates/${templateId}/items/${itemId}`, {method: 'DELETE'});
            } catch (err) {
                onError(err);
            }
        },
        [onError],
    );

    const reorderTemplates = useCallback(
        (movedId, afterId, reordered) => {
            setTemplates(reordered);
            apiFetch(`/checklist-templates/${movedId}`, {
                method: 'PUT',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({after: afterId}),
            }).catch(err => onError(err));
        },
        [onError],
    );

    const toggleViewMode = useCallback(mode => {
        setViewMode(mode);
        localStorage.setItem('templateViewMode', mode);
    }, []);

    const templateSensors = useSensors(
        useSensor(PointerSensor, {activationConstraint: {distance: 8}}),
        useSensor(KeyboardSensor, {coordinateGetter: sortableKeyboardCoordinates}),
    );

    function handleTemplateDragEnd(event) {
        const {active, over} = event;
        if (!over || active.id === over.id) return;
        const oldIndex = templates.findIndex(t => t.id === active.id);
        const newIndex = templates.findIndex(t => t.id === over.id);
        const reordered = arrayMove(templates, oldIndex, newIndex);
        const afterId = newIndex > 0 ? reordered[newIndex - 1].id : null;
        reorderTemplates(active.id, afterId, reordered);
    }

    if (loading) {
        return (
            <div className="checklist-container-root template-container">
                <Spinner size={28} className="spinner-center" />
            </div>
        );
    }

    return (
        <div className="checklist-container-root template-container">
            <div className="template-toolbar">
                <button className="add-checklist-button" onClick={() => setShowModal(true)}>
                    + New Template
                </button>
                <div className="view-toggle">
                    <button
                        className={`view-toggle-btn${viewMode === 'grid' ? ' active' : ''}`}
                        onClick={() => toggleViewMode('grid')}
                        title="Grid view"
                    >
                        <GridIcon />
                    </button>
                    <button
                        className={`view-toggle-btn${viewMode === 'columns' ? ' active' : ''}`}
                        onClick={() => toggleViewMode('columns')}
                        title="Column view"
                    >
                        <ColumnsIcon />
                    </button>
                </div>
                {showModal && <NewTemplateModal onConfirm={handleCreate} onCancel={() => setShowModal(false)} />}
                {spawnTemplateId && (
                    <NewChecklistModal
                        templates={templates}
                        groups={groups}
                        preselectedTemplateId={spawnTemplateId}
                        onConfirm={handleSpawnConfirm}
                        onCancel={() => setSpawnTemplateId(null)}
                    />
                )}
            </div>
            <DndContext sensors={templateSensors} collisionDetection={closestCenter} onDragEnd={handleTemplateDragEnd}>
                <SortableContext
                    items={templates.map(t => t.id)}
                    strategy={viewMode === 'grid' ? rectSortingStrategy : horizontalListSortingStrategy}
                >
                    <div className={`checklists-container${viewMode === 'grid' ? ' templates-grid' : ''}`}>
                        {templates.map(template => (
                            <SortableTemplateCard
                                key={template.id}
                                template={template}
                                onUpdateTemplate={handleUpdateTemplate}
                                onDelete={handleDelete}
                                onUse={handleUse}
                                onExpand={handleExpand}
                                onAddItem={handleAddItem}
                                onUpdateItem={handleUpdateItem}
                                onReorderItems={handleReorderItems}
                                onDeleteItem={handleDeleteItem}
                            />
                        ))}
                    </div>
                </SortableContext>
            </DndContext>
        </div>
    );
}
